import { type FC } from 'react'
import { NavLink } from 'react-router-dom'
import { type AccountSchedulingSettings, LocationKind } from '../../../../models/Scheduling'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import {
  useCreateSchedulingLocationMutation,
  useDeleteSchedulingLocationMutation,
  useGetSchedulingLocationsQuery,
  useUpdateSchedulingLocationMutation,
} from '../../../../store/scheduling/scheduling'
import { useUserTimezone } from '../../../../common/useUserTimezone'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { LocationsEditor } from '../../../Scheduling/shared/LocationsEditor'
import { DefaultAvailabilityEditor } from '../../../Scheduling/shared/DefaultAvailabilityEditor'
import { formatTimezoneLabel } from '../../../Scheduling/shared/schedulingDisplay'
import { type UpdateAccountSettings } from '../SchedulingSetupTab'
import { SettingsCard, SettingsRow, SettingsSection, SettingsToggle } from './SettingsSection'

interface SectionProps {
  settings: AccountSchedulingSettings
  readOnly: boolean
  isSaving?: boolean
  onUpdate: UpdateAccountSettings
}

export const AvailabilitySection: FC<SectionProps> = ({ settings, readOnly, isSaving = false, onUpdate }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { tracked } = useTracking()
  const timezone = useUserTimezone()
  const { data: locations, isLoading: isLoadingLocations, isFetching: isFetchingLocations } = useGetSchedulingLocationsQuery({ accountId })
  const [createLocation, { isLoading: isCreatingLocation }] = useCreateSchedulingLocationMutation()
  const [updateLocation, { isLoading: isUpdatingLocation }] = useUpdateSchedulingLocationMutation()
  const [deleteLocation, { isLoading: isDeletingLocation }] = useDeleteSchedulingLocationMutation()
  const isSavingLocations = isCreatingLocation || isUpdatingLocation || isDeletingLocation || (isFetchingLocations && !isLoadingLocations)

  return (
    <SettingsSection title='Availability' subtitle='Set defaults to prefill fields on new shared event types.' isSaving={isSaving || isSavingLocations}>
      <SettingsCard>
        <SettingsRow>
          <LocationsEditor
            locations={locations ?? []}
            readOnly={readOnly}
            description='Set up your office locations to offer in-person meetings to your clients.'
            defaultLocationId={settings.defaults.locationId}
            defaultLabel='Account default'
            defaultKind={LocationKind.ACCOUNT_DEFAULT}
            onCreate={tracked((input) => void createLocation({ accountId, input }), {
              eventName: TrackEvent.SCHEDULING_LOCATION_CREATED,
              eventDetails: { scope: 'account' },
            })}
            onUpdate={tracked((locationId, input) => void updateLocation({ accountId, locationId, input }), {
              eventName: TrackEvent.SCHEDULING_LOCATION_UPDATED,
              eventDetails: { scope: 'account' },
            })}
            onDelete={tracked((locationId) => void deleteLocation({ accountId, locationId }), {
              eventName: TrackEvent.SCHEDULING_LOCATION_DELETED,
              eventDetails: { scope: 'account' },
            })}
            onSetDefault={(locationId) => onUpdate({ defaults: { ...settings.defaults, locationId } })}
          />
        </SettingsRow>
      </SettingsCard>
      <SettingsCard className='zocks-bg-color-gray-100'>
        <SettingsRow className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column flex-grow-1'>
            <h6 className='mb-0 zocks-text-gray-900'>Timezone</h6>
            <span className='paragraph-small zocks-text-gray-700'>
              Your account default timezone is managed under your <NavLink to='/workspace/setup/settings/account'>account settings</NavLink>. Your weekly hours
              are interpreted in this timezone.
            </span>
          </div>
          <span className='paragraph-small zocks-text-gray-700 text-nowrap'>{formatTimezoneLabel(timezone)}</span>
        </SettingsRow>
      </SettingsCard>
      <SettingsCard>
        <SettingsRow>
          <SettingsToggle
            label='Federal holidays'
            description={
              <>
                Clients can&apos;t book meetings on US federal holidays or the weekdays they&apos;re observed on. Applies to all booking pages in your account.
                <br />
                Each user can override this setting.
              </>
            }
            checked={settings.defaults.excludeHolidays}
            readOnly={readOnly}
            ariaLabel='Exclude federal holidays'
            labelClassName='h6 mb-0 zocks-text-gray-900'
            onChange={(excludeHolidays) => onUpdate({ defaults: { ...settings.defaults, excludeHolidays } })}
          />
        </SettingsRow>
      </SettingsCard>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column'>
            <h6 className='mb-0 zocks-text-gray-900'>Weekly hours</h6>
            <span className='paragraph-small zocks-text-gray-600'>
              Set the default weekly availability for meetings. Buffers extend beyond these hours, before and after each slot.
            </span>
          </div>
          <DefaultAvailabilityEditor
            availability={settings.defaults.availabilitySchedule}
            readOnly={readOnly}
            onAvailabilityChange={(availabilitySchedule) => onUpdate({ defaults: { ...settings.defaults, availabilitySchedule } })}
          />
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  )
}
